import { computed, Ref, ref, watch } from 'vue';

export interface Step {
  id: string;
  available?: boolean;
}

export function useSteps(steps: Ref<Step[]>) {
  const currentStepIndex = ref<number>(0);

  const currentStep = computed<Step | undefined>(() => steps.value[currentStepIndex.value]);

  watch(
    steps,
    () => {
      if (steps.value.length) {
        currentStepIndex.value =
          steps.value.findIndex((step) => step.id === currentStep.value?.id) || 0;
      }
    },
    {
      immediate: true,
    },
  );

  function inStep(stepId: string) {
    return currentStep.value?.id === stepId;
  }

  function available(stepIndex: number) {
    const step: Step | undefined = steps.value[stepIndex];

    return step && (step.available === undefined || step.available);
  }

  const inLastStep = computed(() => currentStepIndex.value === steps.value.length - 1);

  const inFirstStep = computed(() => currentStepIndex.value === 0);

  const nextAllowed = computed(() => !inLastStep.value && available(currentStepIndex.value + 1));

  const backAllowed = computed(() => !inFirstStep.value && available(currentStepIndex.value - 1));

  function next() {
    if (!inLastStep.value) {
      currentStepIndex.value = currentStepIndex.value + 1;
    }
  }

  function back() {
    if (!inFirstStep.value) {
      currentStepIndex.value = currentStepIndex.value - 1;
    }
  }

  function reset() {
    currentStepIndex.value = 0;
  }

  function status() {
    currentStepIndex.value = steps.value.length - 1;
  }

  return {
    currentStep,
    inStep,
    inLastStep,
    inFirstStep,
    next,
    back,
    nextAllowed,
    backAllowed,
    reset,
    status,
  };
}
